/**
 * MethodSelectionSection Component
 * Card-based layout for per-file method selection with industrial precision aesthetic
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import SubmitButton from '../../../shared/components/buttons/SubmitButton';
import { Alert, AlertTitle, AlertDescription } from '../../../shared/components/feedback/Alert';
import type { FileMethod } from '../types/dataAdjustments.types';
import { PROCESSING_METHOD_OPTIONS } from '../types/dataAdjustments.types';
import DecimalPrecisionSection from '@/shared/components/forms/selectors/DecimalPrecisionSection';
import type { DecimalPrecision } from '@/types/common';

interface MethodSelectionSectionProps {
  filesNeedingMethods: FileMethod[];
  selectedMethods: { [key: string]: string };
  intrplMaxValues: { [key: string]: string };
  decimalPlaces: { [key: string]: string };
  onMethodChange: (filename: string, method: string) => void;
  onIntrplMaxChange: (filename: string, value: string) => void;
  onDecimalPlacesChange: (filename: string, value: string) => void;
  onSubmit: () => void;
  isLoading: boolean;
  allMethodsSelected: boolean;
}

const needsMaxMinutes = (method: string): boolean =>
  method === 'intrpl' || method === 'nearest (max. delta)';

const selectClass =
  'w-full border border-border rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card appearance-none transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary';

const labelClass =
  'text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground';

const MethodSelectionSection: React.FC<MethodSelectionSectionProps> = ({
  filesNeedingMethods,
  selectedMethods,
  intrplMaxValues,
  decimalPlaces,
  onMethodChange,
  onIntrplMaxChange,
  onDecimalPlacesChange,
  onSubmit,
  isLoading,
  allMethodsSelected
}) => {
  const { t } = useTranslation('dataAdjustments');

  if (filesNeedingMethods.length === 0) return null;

  return (
    <>
      {/* Alert notification */}
      <div className="mb-4">
        <Alert variant="default">
          <AlertTitle>{t('methodSelection.alertTitle')}</AlertTitle>
          <AlertDescription>
            {t('methodSelection.alertDescription')}
          </AlertDescription>
        </Alert>
      </div>

      {/* Section heading */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4">
          {t('methodSelection.title')}
        </h2>

        {/* File cards */}
        <div className="space-y-4">
          {filesNeedingMethods.map((fileInfo, index) => {
            const method = selectedMethods[fileInfo.filename] || '';

            return (
              <div
                key={fileInfo.filename}
                className="rounded-xl border border-border bg-card shadow-sm overflow-hidden animate-fadeIn hover:shadow-md transition-shadow duration-200"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                {/* Card header */}
                <div className="bg-gradient-to-r from-muted to-muted border-b border-border/60 px-4 py-3 sm:px-5 sm:py-3.5 flex items-center justify-between">
                  <span className="text-sm sm:text-base font-semibold text-foreground truncate">
                    {fileInfo.filename}
                  </span>
                  <span className="ml-3 shrink-0 inline-flex items-center gap-1.5 bg-accent text-accent-foreground border border-primary/20 rounded-md px-2.5 py-1 text-[10px] sm:text-xs uppercase tracking-wide font-medium">
                    {fileInfo.current_timestep}min
                    <span className="text-primary/60">&rarr;</span>
                    {fileInfo.requested_timestep}min
                  </span>
                </div>

                {/* Card body */}
                <div className="p-4 sm:p-5 space-y-4">
                  {/* Processing method select */}
                  <div>
                    <label className={`${labelClass} block mb-1.5`}>
                      {t('methodSelection.processingMethod')}
                    </label>
                    <div className="relative">
                      <select
                        value={method}
                        onChange={(e) => onMethodChange(fileInfo.filename, e.target.value)}
                        className={selectClass}
                      >
                        <option value="" disabled>
                          {t('methodSelection.placeholder')}
                        </option>
                        {PROCESSING_METHOD_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.label)}
                          </option>
                        ))}
                      </select>
                      <svg
                        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Max minutes input — conditional */}
                  {needsMaxMinutes(method) && (
                    <div className="animate-fadeIn">
                      <label className={`${labelClass} block mb-1.5`}>
                        {t('methodSelection.maxMinutesBetweenPoints')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={intrplMaxValues[fileInfo.filename] || ''}
                        onChange={(e) => onIntrplMaxChange(fileInfo.filename, e.target.value)}
                        className="w-full sm:w-40 border border-border rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder={t('methodSelection.minutesPlaceholder')}
                      />
                    </div>
                  )}

                  {/* Decimal places input — conditional */}
                  {method && (
                    <div className="animate-fadeIn">
                      <DecimalPrecisionSection
                        value={decimalPlaces[fileInfo.filename] === 'full' || !decimalPlaces[fileInfo.filename] ? 'full' : (parseInt(decimalPlaces[fileInfo.filename], 10) as DecimalPrecision)}
                        onChange={(newValue: DecimalPrecision) =>
                          onDecimalPlacesChange(fileInfo.filename, newValue === 'full' ? 'full' : String(newValue))
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <SubmitButton
            onClick={onSubmit}
            disabled={!allMethodsSelected || isLoading}
          />
        </div>
      </div>
    </>
  );
};

export default MethodSelectionSection;
